import {
  addMissingFactorsAndSort,
  useCashListConfiguration,
  useCashTransactionGroupAddTransaction,
  useCashTransactionGroupPhoenix,
  useCashTransactionGroups,
} from '@pflegenavi/frontend/api-nursing-home';
import {
  useFeatureFlag,
  useFeatureFlagEnabled,
} from '@pflegenavi/frontend/feature-flags';
import type { RowAction } from '@pflegenavi/web-components';
import {
  LoadingContainer,
  makeConfig,
  PaginatedTable2,
} from '@pflegenavi/web-components';
import {
  CashTransactionGroupType,
  FeatureFlag,
  FeatureFlagStatus,
} from '@pflegenavi/shared/api';
import type { Dispatch, FC, SetStateAction } from 'react';
import React, { useMemo, useState } from 'react';
import {
  useFormatDate,
  useFormatTime,
  useFormatting,
} from '@pflegenavi/frontend/localization';
import { useTranslation } from 'react-i18next';
import { Container, Stack } from '@mui/material';
import { CashManagementHeaderBreadcrumbs } from './CashOverviewPage';
import { ChangeCashMode } from './components/form/model';
import { generatePath, nhAppMainPages } from '@pflegenavi/frontend/routing';
import { useNavigate } from 'react-router-dom';
import type { CashTransactionGroupRowModel } from './components/groupTable/model';
import { formatCashTransactionGroupRowModel } from './components/groupTable/model';
import { TypeColumn } from './components/groupTable/TypeColumn';
import {
  CashAmountColumn,
  CheckColumn,
  DateColumn,
  InfoColumn,
  LinkAmountColumn,
} from './components/groupTable/Columns';
import { AddCashTransactionModal } from './CashTransactionGroupDetailPage';
import { AssignToExpenseManagementModal } from './components/expenseManagement/AssignToExpenseManagementModal';
import { isAssignToExpenseEligible } from './components/expenseManagement/eligibility';
import { useTrackStructuredEvent } from '@pflegenavi/frontend/tracking';
import { CashListDropdownView } from './components/CashListDropdown';
import { useCashListTableQuery } from './useCashListTableQuery';
import { useNursingHomeContext } from '@pflegenavi/frontend/nursing-home-context';
import { keepPreviousData } from '@tanstack/react-query';

type ColumnNames =
  | 'type'
  | 'lastUpdate'
  | 'cashAmount'
  | 'linked'
  | 'check'
  | 'info';

type Filters = never;

const useRowCounts = (cashListId: string, mismatchRiskEnabled: boolean) => {
  const { data: allRows } = useCashTransactionGroups({
    cashListId,
    pageSize: 1,
    page: 0,
    excludeWithExpense: false,
  });
  const { data: depositRows } = useCashTransactionGroups({
    cashListId,
    pageSize: 1,
    page: 0,
    groupType: [
      CashTransactionGroupType.Deposit,
      CashTransactionGroupType.ResidentDeposit,
    ],
    excludeWithExpense: false,
  });
  const { data: withdrawalRows } = useCashTransactionGroups({
    cashListId,
    pageSize: 1,
    page: 0,
    groupType: [
      CashTransactionGroupType.ReceiptWithdrawal,
      CashTransactionGroupType.ResidentWithdrawal,
    ],
    excludeWithExpense: false,
  });

  const { data: transferRows } = useCashTransactionGroups({
    cashListId,
    pageSize: 1,
    page: 0,
    groupType: [CashTransactionGroupType.Transfer],
    excludeWithExpense: false,
  });

  const { data: adjustmentRows } = useCashTransactionGroups({
    cashListId,
    pageSize: 1,
    page: 0,
    groupType: [CashTransactionGroupType.Adjustment],
    excludeWithExpense: false,
  });

  const { data: mismatchRows } = useCashTransactionGroups({
    cashListId,
    pageSize: 1,
    page: 0,
    mismatchOnly: true,
    // Groups already assigned to an expense are handled there, not a mismatch
    excludeWithExpense: true,
  });

  const { data: mismatchRiskRows } = useCashTransactionGroups(
    {
      cashListId,
      pageSize: 1,
      page: 0,
      mismatchRisk: true,
      // Groups already assigned to an expense are handled there, not a mismatch
      excludeWithExpense: true,
    },
    {
      enabled: mismatchRiskEnabled,
    }
  );

  return {
    all: allRows?.meta?.totalItems ?? 0,
    deposit: depositRows?.meta?.totalItems ?? 0,
    withdrawal: withdrawalRows?.meta?.totalItems ?? 0,
    transfer: transferRows?.meta?.totalItems ?? 0,
    adjustment: adjustmentRows?.meta?.totalItems ?? 0,
    mismatch: mismatchRows?.meta?.totalItems ?? 0,
    mismatchRisk: mismatchRiskRows?.meta?.totalItems ?? 0,
  };
};

interface CashTransactionGroupTableProps {
  cashListId: string;
  setOpen: Dispatch<
    SetStateAction<
      | undefined
      | {
          id: string;
          type: 'withdrawal' | 'deposit';
          cashListId: string;
        }
    >
  >;
}

export const CashTransactionGroupTable: FC<CashTransactionGroupTableProps> = ({
  setOpen,
  cashListId,
}) => {
  const fDate = useFormatDate();
  const fTime = useFormatTime();
  const { fCurrencyCents } = useFormatting();
  const { t } = useTranslation();

  const hasAccountingPaymentsNew = useFeatureFlagEnabled(
    FeatureFlag.AccountingPaymentsNew
  );

  const expenseManagementEnabled = useFeatureFlagEnabled(
    FeatureFlag.ExpenseManagement
  );

  const mismatchRiskTabEnabled = useFeatureFlagEnabled(
    FeatureFlag.MismatchRiskTab
  );

  const { pliantReferenceCashListId } = useCashListConfiguration();
  const isPliantReferenceCashList = cashListId === pliantReferenceCashListId;

  const navigate = useNavigate();

  const [selectedType, setSelectedType] = useState<
    CashTransactionGroupType | 'all' | 'mismatch' | 'mismatchRisk'
  >('all');

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(5);

  const [expenseTarget, setExpenseTarget] =
    useState<CashTransactionGroupRowModel | null>(null);

  // The list rows carry no Pliant data, so fetch the selected group to
  // prefill the expense receipt date with the Pliant transaction date.
  const { data: expenseTargetGroup, isLoading: expenseTargetGroupLoading } =
    useCashTransactionGroupPhoenix(expenseTarget?.id);

  const rowCount = useRowCounts(cashListId, mismatchRiskTabEnabled);

  const transferEnabled =
    useFeatureFlag(FeatureFlag.AccountingPayments) ===
    FeatureFlagStatus.Enabled;

  const { data: rows, isLoading } = useCashTransactionGroups(
    {
      cashListId,
      pageSize,
      page,
      sort: 'update_date',
      sortOrder: 'desc',
      fields: [
        'residents',
        'transfer_count',
        'cash_changed',
        'amount_linked',
        'notes',
      ],
      mismatchOnly: selectedType === 'mismatch',
      mismatchRisk: selectedType === 'mismatchRisk',
      groupType:
        selectedType === 'all' ||
        selectedType === 'mismatch' ||
        selectedType === 'mismatchRisk'
          ? undefined
          : selectedType === CashTransactionGroupType.Deposit
          ? [
              CashTransactionGroupType.Deposit,
              CashTransactionGroupType.ResidentDeposit,
            ]
          : selectedType === CashTransactionGroupType.ReceiptWithdrawal
          ? [
              CashTransactionGroupType.ReceiptWithdrawal,
              CashTransactionGroupType.ResidentWithdrawal,
            ]
          : [selectedType],
      excludeWithExpense:
        selectedType === 'mismatch' || selectedType === 'mismatchRisk',
    },
    {
      placeholderData: keepPreviousData,
    }
  );

  const transformed = useMemo(() => {
    return (
      rows?.data.map((cashTransactionGroup) =>
        formatCashTransactionGroupRowModel(cashTransactionGroup, {
          fDate,
          fTime,
          fCurrencyCents,
          t,
          useGenericTypeNames: expenseManagementEnabled,
        })
      ) ?? []
    );
  }, [rows, fDate, fTime, fCurrencyCents, t, expenseManagementEnabled]);

  const options = useMemo(() => {
    return {
      items: (row: CashTransactionGroupRowModel) => {
        const assignExpenseItems: Array<
          RowAction<CashTransactionGroupRowModel>
        > = [];
        if (expenseManagementEnabled && isAssignToExpenseEligible(row)) {
          assignExpenseItems.push({
            key: 'assign-to-expense',
            label: t('cashManagement.actions.assign-to-expense'),
            onClick: (clickedRow) => {
              setExpenseTarget(clickedRow);
            },
          });
        }

        if (
          isPliantReferenceCashList ||
          row.type === CashTransactionGroupType.ResidentDeposit ||
          row.type === CashTransactionGroupType.Transfer ||
          row.type === CashTransactionGroupType.Adjustment
        ) {
          return assignExpenseItems;
        }
        return [
          ...assignExpenseItems,
          {
            label: t('cashManagement.table-row.deposit'),
            onClick: () => {
              setOpen({
                id: row.id,
                type: 'deposit',
                cashListId: row.cashListId,
              });
            },
          },
          {
            label: t('cashManagement.table-row.withdrawal'),
            onClick: () => {
              setOpen({
                id: row.id,
                type: 'withdrawal',
                cashListId: row.cashListId,
              });
            },
          },
        ];
      },
    };
  }, [setOpen, t, expenseManagementEnabled, isPliantReferenceCashList]);

  const groupDetailPath = useFeatureFlagEnabled(
    FeatureFlag.AccountingPaymentsNew
  )
    ? nhAppMainPages.NURSING_HOME_ACCOUNTING_GROUP_DETAIL
    : nhAppMainPages.CASH_MANAGEMENT_GROUP_DETAIL;

  const recentCashTransactionGroupsConfig = useMemo(
    () =>
      makeConfig<CashTransactionGroupRowModel, ColumnNames, Filters>({
        showAll: undefined,
        onClickRow: (row) => {
          navigate(
            generatePath(groupDetailPath, {
              groupId: row.id,
            })
          );
        },
        tabs: {
          selectedItem: selectedType,
          updateFilterModel: setSelectedType,
          items: [
            {
              value: 'all',
              label: t('receiptType.all'),
              color: 'default',
              count: rowCount.all,
            },
            ...(mismatchRiskTabEnabled
              ? [
                  {
                    value: 'mismatchRisk',
                    label: t('cashManagement.table-tab.mismatch-risk'),
                    color: 'warning',
                    count: rowCount.mismatchRisk,
                  } as const,
                ]
              : []),
            {
              value: 'mismatch',
              label: t('cashManagement.table-tab.mismatch'),
              color: 'warning',
              count: rowCount.mismatch,
              separatorAfter: true,
            },
            {
              value: CashTransactionGroupType.Deposit,
              label: t('cashManagement.deposit'),
              color: 'success',
              count: rowCount.deposit,
            },
            {
              value: CashTransactionGroupType.ReceiptWithdrawal,
              label: t('cashManagement.withdrawal'),
              color: 'error',
              count: rowCount.withdrawal,
            },
            ...(transferEnabled
              ? [
                  {
                    value: CashTransactionGroupType.Transfer,
                    label: t('cashManagement.link.table-row.type.transfer'),
                    color: 'default',
                    count: rowCount.transfer,
                  } as const,
                ]
              : []),
            {
              value: CashTransactionGroupType.Adjustment,
              label: t('cashManagement.table-row.adjustment'),
              color: 'info',
              count: rowCount.adjustment,
            },
          ],
        },
        columns: [
          {
            key: 'type',
            label: t('cashManagement.link.table-header.type'),
            align: 'left',
            component: TypeColumn,
          },
          {
            key: 'lastUpdate',
            label: t('cashManagement.link.table-header.lastUpdate'),
            component: DateColumn,
          },
          {
            key: 'cashAmount',
            label: hasAccountingPaymentsNew
              ? t('cashManagement.link.table-header.amount')
              : t('cashManagement.link.table-header.cashAmount'),
            component: CashAmountColumn,
          },
          {
            key: 'linked',
            label: t('cashManagement.link.table-header.linked'),
            component: LinkAmountColumn,
          },
          {
            key: 'check',
            label: t('cashManagement.link.table-header.check'),
            component: CheckColumn,
            align: 'center',
          },
          {
            key: 'info',
            label: t('cashManagement.link.table-header.info'),
            component: InfoColumn,
          },
        ],
        pagination: {
          page,
          pageSize,
          pageSizeOptions: [5, 10],
          total: rows?.meta.totalItems || 0,
          onPageChange: setPage,
          onPageSizeChange: setPageSize,
        },
        options,
      }),
    [
      options,
      page,
      pageSize,
      rows?.meta.totalItems,
      rowCount.adjustment,
      rowCount.transfer,
      rowCount.withdrawal,
      rowCount.deposit,
      rowCount.mismatch,
      rowCount.mismatchRisk,
      mismatchRiskTabEnabled,
      selectedType,
      t,
      navigate,
      rowCount.all,
      transferEnabled,
      groupDetailPath,
      hasAccountingPaymentsNew,
    ]
  );

  return (
    <>
      <PaginatedTable2
        name="CashTransactionGroupOverview"
        useTrackStructuredEvent={useTrackStructuredEvent}
        config={recentCashTransactionGroupsConfig}
        rows={transformed}
        isLoading={isLoading}
      />
      {expenseTarget && !expenseTargetGroupLoading && (
        <AssignToExpenseManagementModal
          open
          onClose={() => setExpenseTarget(null)}
          cashTransactionGroupId={expenseTarget.id}
          groupAmountCents={Math.abs(expenseTarget.cashChangedCents ?? 0)}
          pliantTransactionCreatedAt={expenseTargetGroup?.pliantTransactionCreatedAt?.toISOString()}
        />
      )}
    </>
  );
};

const CashOverviewPageV3: FC = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState<
    | undefined
    | {
        id: string;
        type: 'withdrawal' | 'deposit';
        cashListId: string;
      }
  >(undefined);

  const { cashListId, setCashListId, cashLists } = useCashListTableQuery();
  const { selectedNursingHome } = useNursingHomeContext();

  const { mutateAsync: addTransaction } = useCashTransactionGroupAddTransaction(
    selectedNursingHome?.id
  );

  const hasAccountingPaymentsNew = useFeatureFlagEnabled(
    FeatureFlag.AccountingPaymentsNew
  );
  const pageTitle = hasAccountingPaymentsNew
    ? t('accounting.nav.transaction-groups')
    : t('nav.cash-management.overview');

  if (!cashListId) {
    return (
      <Container maxWidth={'lg'}>
        <CashManagementHeaderBreadcrumbs pageTitle={pageTitle} />
        <LoadingContainer />
      </Container>
    );
  }

  return (
    <Container maxWidth={'lg'}>
      <CashManagementHeaderBreadcrumbs pageTitle={pageTitle} />

      <Stack direction="column" gap={3}>
        {hasAccountingPaymentsNew && (
          <CashListDropdownView
            selectedCashListId={cashListId}
            setSelectedCashListId={setCashListId}
            cashLists={cashLists}
          />
        )}
        <CashTransactionGroupTable cashListId={cashListId} setOpen={setOpen} />

        <AddCashTransactionModal
          cashListId={open?.cashListId ?? ''}
          open={Boolean(open)}
          onClose={() => setOpen(undefined)}
          type={
            open?.type === 'withdrawal'
              ? ChangeCashMode.Withdraw
              : ChangeCashMode.Deposit
          }
          onAddCashTransactionGroup={(
            type,
            coins,
            bankAccountAmount,
            notes
          ) => {
            if (!open) {
              return;
            }
            const coinsSorted =
              coins !== undefined && coins.length > 0
                ? addMissingFactorsAndSort(coins).map((coin) => coin.amount)
                : undefined;
            addTransaction({
              cashTransactionGroupId: open.id,
              type: type === ChangeCashMode.Withdraw ? 'withdrawal' : 'deposit',
              coins: coinsSorted,
              bankAccountAmount: bankAccountAmount,
              notes,
            });
          }}
        />
      </Stack>
    </Container>
  );
};

export default CashOverviewPageV3;
